import { isEqual, isNil, sortBy } from 'lodash';
import { IActivity, ISection } from 'typings/doenkids/doenkids';
import { IAiAdjustmentResponse, TAiAdjustmentSectionAction } from 'typings/api-activity';
import { ESectionType, getNameForSectionType } from 'src/components/shared/section/section.component';

/**
 * What a comparison row represents.
 *
 * FIELD and SECTION rows can be changed by a proposal. CONTEXT rows are shown
 * for orientation only: the AI never touches duration or preparation, and the
 * prototype shows them so the reader can see they are untouched.
 */
export enum EAiComparisonRowKind {
  FIELD = 'field',
  SECTION = 'section',
  CONTEXT = 'context',
}

/** One row of the old-versus-new comparison. */
export interface IAiComparisonRow {
  /** Stable key for template tracking. Section rows cannot use the id: created sections all carry -1 */
  key: string;
  kind: EAiComparisonRowKind;
  /** The activity property this row shows, for FIELD and CONTEXT rows */
  field: 'name' | 'subtitle' | 'summary' | 'duration' | 'preparation' | null;
  /** Translation key for the row label, or null when the type is unknown to this platform */
  labelKey: string | null;
  /** The raw type name, shown only when there is no translation key to use */
  labelFallback: string | null;
  /** True when the proposal differs from what is stored */
  changed: boolean;

  /** FIELD and CONTEXT rows: the stored text */
  oldValue?: string | number | null;
  /** FIELD and CONTEXT rows: the proposed text */
  newValue?: string | number | null;

  /** SECTION rows: the platform section id, or -1 for a created section */
  sectionId?: number;
  /** SECTION rows: what the proposal does to this section */
  action?: TAiAdjustmentSectionAction;
  /** SECTION rows: the platform section type, or null when unknown to this platform */
  sectionTypeId?: number | null;
  /** SECTION rows: the stored section data, null for a created section */
  oldData?: Record<string, unknown> | null;
  /** SECTION rows: the proposed section data, null for a deleted section */
  newData?: Record<string, unknown> | null;
  /** SECTION rows: true when the proposal moves this section to another position */
  reordered?: boolean;
  /** SECTION rows: true when this section cannot be persisted, because the platform has no such type */
  unsupported?: boolean;
}

/** A section update to send, already stripped of the proposal-only fields. */
export interface IAiSectionUpdate {
  id: number;
  activity_id: number;
  type_id: number;
  name: string;
  data: Record<string, unknown>;
  order: number;
  columns?: number;
}

/**
 * A section to create from the proposal.
 *
 * Deliberately without `activity_id`: the same plan is used to write onto the
 * adjusted activity and onto a fresh copy, so the target is the caller's to
 * supply.
 */
export interface IAiSectionCreate {
  type_id: number;
  name: string;
  data: Record<string, unknown>;
  order: number;
  /** Carried from the source section when there is one; a new section takes the API default */
  columns?: number;
}

/**
 * Everything the accept flow has to write to the section table, worked out
 * before a single request goes out.
 */
export interface IAiSectionWritePlan {
  updates: IAiSectionUpdate[];
  creates: IAiSectionCreate[];
  /** Section ids to archive */
  archives: number[];
  /** Type names of created sections that had to be dropped, for reporting to the user */
  skipped: string[];
}

/**
 * Resolves the label for a section row.
 *
 * The platform type id is authoritative: content-ai-api keys its logic on the
 * type NAME and the two use different spellings ('activity-what' in the
 * catalogue, 'activity_what' in the translation keys), so the name is only used
 * when there is no id to translate.
 */
function resolveSectionLabel(typeId: number | null, typeName: string): { labelKey: string | null; labelFallback: string | null } {
  if (isNil(typeId)) {
    return { labelKey: null, labelFallback: typeName };
  }

  const name = getNameForSectionType(typeId as ESectionType);

  if (name === 'unknown') {
    return { labelKey: null, labelFallback: typeName };
  }

  return { labelKey: `activity.section.type.${name}.title`, labelFallback: null };
}

/**
 * Normalises a field for comparison, so an absent value and an empty one are
 * the same thing.
 *
 * The activity carries `undefined` for a field that was never filled in while
 * the proposal carries `null`, and neither is a change the user should see
 * highlighted.
 */
function normaliseField(value: string | null | undefined): string {
  return isNil(value) ? '' : `${value}`.trim();
}

/**
 * Section data keys holding text a proposal could rewrite.
 *
 * Media is deliberately absent: the renderer reports it as a count because the
 * AI never touches it, so a media-only section has nothing to put side by side.
 */
const SECTION_TEXT_KEYS = ['content'];
const SECTION_TEXT_LIST_KEYS = ['items', 'material', 'term', 'source', 'video'];

/**
 * Whether a section carries anything worth showing two copies of.
 *
 * A title alone does not count. If the proposal rewrote it the row is kept
 * anyway for being changed, so the only rows this drops are headings and
 * media whose two sides are identical.
 */
function sectionHasComparableText(data: Record<string, unknown> | null | undefined): boolean {
  if (isNil(data)) {
    return false;
  }

  const hasText = SECTION_TEXT_KEYS.some((key) => `${data[key] ?? ''}`.trim().length > 0);

  if (hasText) {
    return true;
  }

  return SECTION_TEXT_LIST_KEYS.some((key) => Array.isArray(data[key]) && (data[key] as unknown[]).length > 0);
}

/**
 * Builds one comparison row per activity field and per proposed section.
 *
 * Pure by design: this is the only place that reasons about `_action`,
 * `_index`, the non-unique `-1` id of created sections, and a `type_id` this
 * platform does not know, so it is also the only place that needs testing for
 * those.
 *
 * @param activity The activity as currently stored
 * @param sections The activity's current sections
 * @param proposal The AI proposal
 * @returns The rows to render, activity fields first, then sections in proposed display order
 */
export function buildComparisonRows(
  activity: IActivity,
  sections: ISection[],
  proposal: IAiAdjustmentResponse,
): IAiComparisonRow[] {
  const rows: IAiComparisonRow[] = [
    {
      key: 'field:name',
      kind: EAiComparisonRowKind.FIELD,
      field: 'name',
      labelKey: 'activity.name',
      labelFallback: null,
      changed: normaliseField(activity?.name) !== normaliseField(proposal.name),
      oldValue: activity?.name ?? null,
      newValue: proposal.name ?? null,
    },
    {
      key: 'field:subtitle',
      kind: EAiComparisonRowKind.FIELD,
      field: 'subtitle',
      labelKey: 'activity.subtitle.label',
      labelFallback: null,
      changed: normaliseField(activity?.subtitle) !== normaliseField(proposal.subtitle),
      oldValue: activity?.subtitle ?? null,
      newValue: proposal.subtitle ?? null,
    },
    {
      key: 'field:summary',
      kind: EAiComparisonRowKind.FIELD,
      field: 'summary',
      labelKey: 'activity.summary.label',
      labelFallback: null,
      changed: normaliseField(activity?.summary) !== normaliseField(proposal.summary),
      oldValue: activity?.summary ?? null,
      newValue: proposal.summary ?? null,
    },
  ];

  // The sections in proposed display order. `_index` is the model's own
  // ordering across the whole response, tombstones included, and is not the
  // same as `order`, which is null for a deleted section.
  //
  const proposedSections = sortBy(proposal.sections ?? [], (section) => section._index);

  const sectionById = new Map<number, ISection>();
  for (const section of sections ?? []) {
    if (!isNil(section?.id)) {
      sectionById.set(section.id, section);
    }
  }

  proposedSections.forEach((proposed, position) => {
    const isCreated = proposed._action === 'created';

    // A created section has no platform row yet and every created section in a
    // proposal carries -1, so it can only be identified by its position. Using
    // the id here would pair it with an unrelated existing section.
    //
    const current = isCreated ? undefined : sectionById.get(proposed.id);
    const { labelKey, labelFallback } = resolveSectionLabel(proposed.type_id, proposed.type);

    const oldData = isNil(current) ? null : ((current.data ?? {}) as Record<string, unknown>);
    const newData = proposed._action === 'deleted' ? null : (proposed.data ?? {});

    const changed = proposed._action !== 'none';
    const reordered = !isNil(current) && !isNil(proposed.order) && current.order !== proposed.order;

    // A row nobody can act on is noise in a dialog meant for judging edits.
    // Anything the proposal touched stays, so this only ever drops sections
    // that are identical on both sides AND have no text to compare: page
    // breaks, and media the AI cannot change anyway.
    //
    if (!changed && !reordered && !sectionHasComparableText(oldData) && !sectionHasComparableText(newData)) {
      return;
    }

    rows.push({
      key: isCreated ? `section:new:${position}` : `section:${proposed.id}`,
      kind: EAiComparisonRowKind.SECTION,
      field: null,
      labelKey,
      labelFallback,
      changed,
      sectionId: proposed.id,
      action: proposed._action,
      sectionTypeId: proposed.type_id,
      oldData,
      newData,
      reordered,
      unsupported: isCreated && isNil(proposed.type_id),
    });
  });

  // Context rows sit at the end, after everything the proposal can actually
  // change, so the reader meets the edits first.
  //
  rows.push(
    {
      key: 'context:duration',
      kind: EAiComparisonRowKind.CONTEXT,
      field: 'duration',
      labelKey: 'activity.duration.label',
      labelFallback: null,
      changed: false,
      oldValue: activity?.duration ?? null,
      newValue: activity?.duration ?? null,
    },
    {
      key: 'context:preparation',
      kind: EAiComparisonRowKind.CONTEXT,
      field: 'preparation',
      labelKey: 'activity.preparation_time.label',
      labelFallback: null,
      changed: false,
      oldValue: activity?.preparation ?? null,
      newValue: activity?.preparation ?? null,
    },
  );

  return rows;
}

/**
 * Works out every section write an accepted proposal needs.
 *
 * Separated from issuing the requests so the decisions are testable without a
 * network: which sections are updated, created, archived or left alone, and
 * which created sections have to be dropped because this platform has no type
 * for them.
 *
 * `_action` and `_index` never reach a write: they are proposal metadata, not
 * section data.
 *
 * @param sections The activity's current sections
 * @param proposal The accepted proposal
 * @returns The writes to perform, plus the type names that were skipped
 */
export function planSectionWrites(sections: ISection[], proposal: IAiAdjustmentResponse): IAiSectionWritePlan {
  const plan: IAiSectionWritePlan = {
    updates: [],
    creates: [],
    archives: [],
    skipped: [],
  };

  const sectionById = new Map<number, ISection>();
  for (const section of sections ?? []) {
    if (!isNil(section?.id)) {
      sectionById.set(section.id, section);
    }
  }

  const proposedSections = sortBy(proposal.sections ?? [], (section) => section._index);

  for (const proposed of proposedSections) {
    if (proposed._action === 'created') {
      // A type this platform does not have cannot be persisted. Reported rather
      // than thrown: the generation is already billed and the rest of the
      // proposal is still worth accepting.
      //
      if (isNil(proposed.type_id)) {
        plan.skipped.push(proposed.type);
        // eslint-disable-next-line no-continue
        continue;
      }

      plan.creates.push({
        type_id: proposed.type_id,
        name: proposed.type,
        data: proposed.data ?? {},
        order: proposed.order ?? 1,
      });

      // eslint-disable-next-line no-continue
      continue;
    }

    const current = sectionById.get(proposed.id);

    // The activity may have changed under the proposal - a section removed in
    // another tab, say. Nothing to write for a section that is no longer there.
    //
    if (isNil(current)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (proposed._action === 'deleted') {
      plan.archives.push(current.id);
      // eslint-disable-next-line no-continue
      continue;
    }

    const data = proposed._action === 'none'
      ? ((current.data ?? {}) as Record<string, unknown>)
      : (proposed.data ?? {});
    const order = proposed.order ?? current.order;

    // An untouched section that has not moved needs no request at all.
    //
    if (proposed._action === 'none' && order === current.order) {
      // eslint-disable-next-line no-continue
      continue;
    }

    plan.updates.push({
      id: current.id,
      activity_id: current.activity_id,
      type_id: current.type_id,
      name: current.name,
      data: isEqual(data, current.data) ? ((current.data ?? {}) as Record<string, unknown>) : data,
      order,
      columns: current.columns,
    });
  }

  return plan;
}

/**
 * The four user-facing failure states ES-2372 §4 requires, each with its own
 * message.
 *
 * The distinction that matters is GUARDRAIL versus UNAVAILABLE versus
 * PROVIDER: a guardrail denial is a decision about the input, UNAVAILABLE
 * means the platform-wide spending cap tripped and must promise no retry, and
 * PROVIDER is transient and should invite one. BUDGET is the organisation's own
 * monthly quota running out.
 */
export enum EAiAdjustmentFailure {
  /** The guardrail refused one of the inputs. A decision, not a fault */
  GUARDRAIL = 'guardrail',
  /** This customer's own token budget for the period is spent */
  BUDGET = 'budget',
  /** The platform's provider account is capped or unbilled */
  UNAVAILABLE = 'unavailable',
  /** Something transient. The only state worth offering a retry for */
  PROVIDER = 'provider',
  /** AI is switched off for this user or this organisation, or has no quota period */
  NOT_ENABLED = 'not_enabled',
  /** The activity was archived or removed while the editor was open */
  ACTIVITY_GONE = 'activity_gone',
  /** The API refused the request itself, e.g. a vision was asked for and none is stored */
  INVALID_REQUEST = 'invalid_request',
}

/**
 * Request inputs the guardrail can name as the one it refused.
 *
 * content-ai-api's `errorEnvelope.error.denied_input`
 * (`src/validators/envelope.ts`). Note `vision`, not `organization_vision`:
 * the field is named for the text, not for where the portal stores it.
 */
export type TAiDeniedInput = 'activity' | 'instruction' | 'vision';

const DENIED_INPUTS: TAiDeniedInput[] = ['activity', 'instruction', 'vision'];

/**
 * Why the guardrail refused an input.
 *
 * content-ai-api's `DENY_CATEGORIES`
 * (`src/services/guardrail/deny-categories.ts`), which states that the order
 * here is deliberately the same as there so the two lists can be checked
 * against each other by eye. New categories are appended at the end.
 *
 * Its `FALLBACK_DENY_CATEGORY` ('prompt_denied', reported when the model
 * denied without naming a reason) is deliberately absent: an honest "no reason
 * given" beats showing one we made up.
 */
export type TAiDenyCategory =
  | 'explicit_content'
  | 'harmful_or_unsafe_for_children'
  | 'hate_or_discrimination'
  | 'off_topic'
  | 'prompt_injection'
  | 'personal_data';

const DENY_CATEGORIES: TAiDenyCategory[] = [
  'explicit_content',
  'harmful_or_unsafe_for_children',
  'hate_or_discrimination',
  'off_topic',
  'prompt_injection',
  'personal_data',
];

/** Everything a failed adjustment tells us, beyond which screen to show. */
export interface IAiAdjustmentFailureDetails {
  failure: EAiAdjustmentFailure;
  /** Which input the guardrail refused, when it named one this platform knows */
  deniedInput: TAiDeniedInput | null;
  /** Why it was refused, when the guardrail named a reason this platform knows */
  category: TAiDenyCategory | null;
  /** The ledger row the attempt was recorded as, when the API reported one */
  aiAdjustmentId: number | null;
}

/**
 * Peer error codes that mean a project-level provider outage — billing off,
 * project quota spent, a tripped spending cap — rather than something
 * transient. content-ai-api logs `[AI-FEATURE-UNAVAILABLE]` for these and
 * states that no retry fixes them; every enabled organisation is degraded at
 * that moment.
 *
 * Its full vocabulary is in that service's README: invalid_request,
 * unauthorized, prompt_denied, internal_error, model_error,
 * guardrail_unavailable, provider_unavailable, section_mismatch,
 * model_timeout. Everything not named here or below stays retryable, which is
 * the safe misclassification: it offers a retry that may not help, where the
 * reverse would withhold one that would have worked. model_error and
 * section_mismatch are deliberately in that group — the model's output varies
 * between calls, so a second attempt can genuinely succeed.
 */
const PLATFORM_UNAVAILABLE_ERROR_CODES = ['provider_unavailable'];

/**
 * Peer error codes that mean the request itself was refused rather than that
 * anything went wrong: a malformed body, or an input over a size ceiling.
 */
const PEER_INVALID_REQUEST_ERROR_CODES = ['invalid_request'];

/**
 * Picks the failure state for a rejected adjustment request.
 *
 * @param error The rejected HTTP response
 * @returns Which of the messages to show
 */
export function classifyAdjustmentFailure(error: any): EAiAdjustmentFailure {
  return describeAdjustmentFailure(error).failure;
}

/**
 * Reads everything useful off a rejected adjustment request.
 *
 * The API distinguishes its own refusals from the peer's, and the two must not
 * be collapsed: a 403 because AI is switched off will never succeed however
 * often it is retried, while a 502 from the peer very well might. Only
 * PROVIDER is transient; every other state is terminal for this request.
 *
 * @param error The rejected HTTP response
 * @returns The failure state, plus any guardrail detail the API forwarded
 */
export function describeAdjustmentFailure(error: any): IAiAdjustmentFailureDetails {
  const status = error?.status ?? error?.code;
  const body = error?.error ?? error;
  const name = body?.name;
  const peer = body?.errors?.error;
  const peerCode = `${peer?.code ?? ''}`;

  const rawDeniedInput = `${peer?.denied_input ?? ''}`;
  const parsedId = parseInt(`${body?.errors?.ai_adjustment_id}`, 10);

  const details: Omit<IAiAdjustmentFailureDetails, 'failure'> = {
    // An input this platform does not know is dropped rather than shown: the
    // copy names the field, and naming one that is not on screen helps nobody.
    //
    deniedInput: DENIED_INPUTS.includes(rawDeniedInput as TAiDeniedInput) ? (rawDeniedInput as TAiDeniedInput) : null,
    category: DENY_CATEGORIES.includes(`${peer?.category ?? ''}` as TAiDenyCategory)
      ? (`${peer.category}` as TAiDenyCategory)
      : null,
    aiAdjustmentId: Number.isFinite(parsedId) ? parsedId : null,
  };

  const describe = (failure: EAiAdjustmentFailure): IAiAdjustmentFailureDetails => ({ failure, ...details });

  // Both peer outcomes are recognised by name rather than by status: the API
  // forwards the peer's status, which is not a fixed value and overlaps with
  // the statuses this API uses for its own refusals.
  //
  if (name === 'AI_ADJUSTMENT_DENIED') {
    return describe(EAiAdjustmentFailure.GUARDRAIL);
  }

  if (name === 'AI_ADJUSTMENT_FAILED') {
    if (PLATFORM_UNAVAILABLE_ERROR_CODES.includes(peerCode)) {
      return describe(EAiAdjustmentFailure.UNAVAILABLE);
    }

    if (PEER_INVALID_REQUEST_ERROR_CODES.includes(peerCode)) {
      return describe(EAiAdjustmentFailure.INVALID_REQUEST);
    }

    return describe(EAiAdjustmentFailure.PROVIDER);
  }

  if (status === 429) {
    return describe(EAiAdjustmentFailure.BUDGET);
  }

  // 403 covers both enablement gates and a customer with no quota period at
  // all. None of them is anything the editor can resolve by trying again.
  //
  if (status === 403) {
    return describe(EAiAdjustmentFailure.NOT_ENABLED);
  }

  if (status === 404) {
    return describe(EAiAdjustmentFailure.ACTIVITY_GONE);
  }

  if (status === 400) {
    return describe(EAiAdjustmentFailure.INVALID_REQUEST);
  }

  if (PLATFORM_UNAVAILABLE_ERROR_CODES.includes(peerCode)) {
    return describe(EAiAdjustmentFailure.UNAVAILABLE);
  }

  return describe(EAiAdjustmentFailure.PROVIDER);
}
